import { useState, useEffect } from 'react';
import { Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip, Tabs, Tab } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BusinessIcon from '@mui/icons-material/Business';
import LayersIcon from '@mui/icons-material/Layers';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import './machinegroup.css';
import MachineGroupAdd from './machinegroupadd';
import { customerbasedshift, cleanCustomerId } from '../../Services/app/operatorservice';
import { getShopImage, getShopLabel } from '../../Shared/shopImages';
import MachineGroupEdit from './machinegroupedit';
import { shiftadd } from '../../Services/app/masterservice';
import Swal from 'sweetalert2';

const MachineGroup = ({IsInGroupReg = false}) => {
  const [machineGroups, setMachineGroups] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [dialogOpenCount, setDialogOpenCount] = useState(0);
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editDialogData, setEditDialogData] = useState(null);
  const [viewGroup, setViewGroup] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0=List, 1=Hierarchy

  const handleCloseAddDialog = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    setIsAddDialogOpen(false);
    setDialogOpenCount(prevCount => Math.max(0, prevCount - 1));
    fetchMachineGroups();
  };

  const handleCloseEditDialog = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    setIsEditDialogOpen(false);
    setEditDialogData(null);
    setDialogOpenCount(prevCount => Math.max(0, prevCount - 1));
    fetchMachineGroups();
  };

  useEffect(() => {
    fetchMachineGroups();
  }, []);

  const fetchMachineGroups = async () => {
    const key = 'machinegroups';
    customerbasedshift(customerId, key)
      .then(async (data) => {
        const allMachineGroups = data[0]?.value || [];
        setMachineGroups(allMachineGroups);
      })
      .catch(error => {
        console.error("Error fetching machine groups:", error);
        setMachineGroups([]);
      });
  };

  const handleOpenAddDialog = () => {
    console.log('Add Machine Group clicked');
    setIsAddDialogOpen(true);
    setDialogOpenCount(prevCount => prevCount + 1);
  };

  const handleOpenEditDialog = (group) => {
    console.log('Edit Machine Group:', group);
    setEditDialogData(group); 
    setIsEditDialogOpen(true);
    setDialogOpenCount(prevCount => prevCount + 1);
  };

  const handleDeleteMachineGroup = (row) => {
    Swal.fire({
      title: 'Are you sure you want to delete this Machine Group?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const key = 'machinegroups';
          const currentDataResponse = await customerbasedshift(customerId, key);
          const allMachineGroups = currentDataResponse[0]?.value || [];
          const updatedMachineGroups = allMachineGroups.filter((group) => {
            if (
              typeof group.id === 'object' &&
              group.id?.$oid &&
              typeof row.id === 'object' &&
              row.id?.$oid
            ) {
              return group.id.$oid !== row.id.$oid;
            }
            return group.id !== row.id;
          });
          const formData = {
            machinegroups: updatedMachineGroups,
            lastUpdateTs: Date.now(),
          };
          const scope = 'SERVER_SCOPE';
          const response = await shiftadd(formData, customerId, scope);
          if (response?.msg) {
            Swal.fire('Deleted!', response.msg, 'success');
          } else {
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: "Machine Group deleted successfully.",
              timer: 1500,
              showConfirmButton: false,
            });
          }
          fetchMachineGroups();
        } catch (error) {
          console.error('Error deleting Machine Group:', error);
          Swal.fire('Error!', 'Failed to delete Machine Group: ' + error.message, 'error');
        }
      } else {
        Swal.fire('Cancelled', '', 'info');
      }
    });
  };

  return (
    <div className="pages" style={{
      paddingBlockStart: IsInGroupReg ? '0px' : '40px',
    }}>
      <div className="pagecontents">
        <div className="left-labels"  style={{
      padding: IsInGroupReg ? '0 2rem 1rem 0' : '1rem 2rem 1rem 0',
    }}>
          <div className="shift-content-1">
            {IsInGroupReg ? (<h6>Create New</h6>) : (<h5>Machine Group List</h5>)}
            <div className="add_new">
              <Tooltip title="Add Machine Group">
                <IconButton className="circle" onClick={handleOpenAddDialog}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
                TabIndicatorProps={{ sx: { background: '#f97316', height: 3 } }}
                sx={{ borderBottom: '1px solid #e5e7eb',
                       '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: 13 },
                       '& .Mui-selected': { color: '#f97316 !important' } }}>
            <Tab label="📋 List" />
            <Tab label="🌳 Hierarchy" />
          </Tabs>
        </div>
        {activeTab === 1 ? (
          <HierarchyView groups={machineGroups} />
        ) : (
        <div className="idle_reason_list">
          {Array.isArray(machineGroups) && machineGroups.length > 0 ? (
            (() => {
              const SHOP_COLORS = {
                MACHINE_SHOP: '#2563eb', DEBURRING: '#f59e0b', SPECIAL_PROCESS: '#dc2626',
                ASSEMBLY: '#16a34a', INSPECTION: '#7c3aed', PACKING: '#6b7280',
              };
              const WORKFLOW_ORDER = ['MACHINE_SHOP','DEBURRING','SPECIAL_PROCESS','ASSEMBLY','INSPECTION','PACKING'];
              const niceShop = (s) => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '(unspecified)';
              // Group by shop_type
              const byShop = {};
              for (const g of machineGroups) {
                const k = g.shop_type || '(unspecified)';
                (byShop[k] = byShop[k] || []).push(g);
              }
              const shopKeys = Object.keys(byShop).sort((a, b) => {
                const ai = WORKFLOW_ORDER.indexOf(a); const bi = WORKFLOW_ORDER.indexOf(b);
                if (ai === -1 && bi === -1) return a.localeCompare(b);
                if (ai === -1) return 1; if (bi === -1) return -1;
                return ai - bi;
              });
              return shopKeys.map(shopKey => {
                const color = SHOP_COLORS[shopKey] || '#6b7280';
                const cells = byShop[shopKey].slice().sort((a, b) => Number(a.code) - Number(b.code));
                const totalMachines = cells.reduce((s, g) => s + (Array.isArray(g.machines) ? g.machines.length : 0), 0);
                return (
                  <div key={shopKey} style={{ width: "100%", marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '8px 12px', background: color + '14',
                                    borderLeft: '4px solid ' + color, borderRadius: '5px 5px 0 0',
                                    color: color, fontWeight: 800, fontSize: 12,
                                    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                      <span>{niceShop(shopKey)}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 10, opacity: 0.8 }}>
                        {cells.length} cell{cells.length !== 1 ? 's' : ''} · {totalMachines} machine{totalMachines !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {cells.map((group, idx) => {
                const itemId = group.id || idx;
                return (
                  <div
                    key={itemId}
                    data-id={group.code}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '32px 56px 1fr auto',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 14px',
                      marginBottom: 8,
                      background: '#fff',
                      border: '1.5px solid #f97316',
                      borderRadius: 8,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                    }}
                  >
                    <span style={{
                      fontSize: 14, fontWeight: 800, color: '#6b7280',
                      textAlign: 'center', fontFamily: 'monospace',
                    }}>{group.code}</span>
                    {group.shop_type ? (
                      <img src={getShopImage(group.shop_type)} alt={getShopLabel(group.shop_type)}
                           style={{ width: 52, height: 52, borderRadius: 6, objectFit: 'cover',
                                     border: '1px solid #e5e7eb', display: 'block' }} />
                    ) : (
                      <div style={{ width: 52, height: 52, borderRadius: 6, background: '#f3f4f6',
                                     border: '1px solid #e5e7eb' }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#111827',
                                     whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {group.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2,
                                     whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {group.shop_type ? getShopLabel(group.shop_type) : '(Shop unspecified)'}
                        {group.building_code ? ' · ' + group.building_code : ''}
                        {group.floor ? ' · Floor ' + group.floor : ''}
                        <span style={{ marginLeft: 8, color: '#9ca3af' }}>
                          · {Array.isArray(group.machines) ? group.machines.length : 0} machine{(Array.isArray(group.machines) ? group.machines.length : 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                      <Tooltip title="View machines">
                        <IconButton size="small" onClick={() => setViewGroup(group)}>
                          <VisibilityIcon fontSize="small" sx={{ color: '#2563eb' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Machine Group">
                        <IconButton size="small" onClick={() => handleOpenEditDialog(group)}>
                          <EditIcon fontSize="small" sx={{ color: '#111827' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Machine Group">
                        <IconButton size="small" onClick={() => handleDeleteMachineGroup(group)}>
                          <DeleteIcon fontSize="small" sx={{ color: '#dc2626' }} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                );
                    })}
                  </div>
                );
              });
            })()
          ) : (
            <div style={{ margin: '2rem', color: '#888' }}>
              No machine groups found.
            </div>
          )}
        </div>
        )}
      </div>
      <MachineGroupAdd
        open={isAddDialogOpen}
        handleClose={handleCloseAddDialog}
        handleAdd={handleCloseAddDialog}
        dialogOpenCount={dialogOpenCount}
        datasource={machineGroups}
        customerId={customerId}
        setDatasource={setMachineGroups}
      />

      {isEditDialogOpen && editDialogData && (
        <MachineGroupEdit
          open={isEditDialogOpen}
          handleClose={handleCloseEditDialog}
          handleAdd={handleCloseEditDialog}
          dialogData={editDialogData}
          dialogOpenCount={dialogOpenCount}
          datasource={machineGroups}
          customerId={customerId}
          setDatasource={setMachineGroups}
        />
      )}
    </div>
  );
};

// Phase 13 2026-06-26: Building -> Floor -> Process -> Cell -> Machine tree
function HierarchyView({ groups }) {
  const [expanded, setExpanded] = useState({});
  const tree = {};
  for (const g of (groups || [])) {
    const b = g.building_code || '(no building)';
    const f = g.floor ? ('Floor ' + g.floor) : '(no floor)';
    const p = g.shop_type || '(unspecified)';
    tree[b] = tree[b] || {};
    tree[b][f] = tree[b][f] || {};
    tree[b][f][p] = tree[b][f][p] || [];
    tree[b][f][p].push(g);
  }
  const buildings = Object.keys(tree).sort();
  if (buildings.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>No machine groups yet.</div>;
  }
  const toggle = (k) => setExpanded(e => ({ ...e, [k]: !e[k] }));
  const SHOP_COLORS = {
    MACHINE_SHOP: '#2563eb', DEBURRING: '#f59e0b', SPECIAL_PROCESS: '#dc2626',
    ASSEMBLY: '#16a34a', INSPECTION: '#7c3aed', PACKING: '#6b7280',
  };
  const niceShop = (s) => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : s;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
      {buildings.map(b => {
        const floors = Object.keys(tree[b]).sort();
        const bKey = 'b:' + b;
        const bOpen = expanded[bKey] !== false;
        const totalCells = floors.reduce((s, f) =>
          s + Object.values(tree[b][f]).reduce((a, arr) => a + arr.length, 0), 0);
        const totalMachines = floors.reduce((s, f) =>
          s + Object.values(tree[b][f]).reduce((a, arr) =>
            a + arr.reduce((aa, g) => aa + (Array.isArray(g.machines) ? g.machines.length : 0), 0), 0), 0);
        return (
          <div key={b} style={{ marginBottom: 4 }}>
            <div onClick={() => toggle(bKey)}
                 style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                          background: '#f97316', color: '#fff', borderRadius: 6, cursor: 'pointer',
                          fontWeight: 800, fontSize: 13 }}>
              {bOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              <BusinessIcon fontSize="small" />
              <span>{b}</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, opacity: 0.9 }}>
                {totalCells} cells · {totalMachines} machines
              </span>
            </div>
            {bOpen && (
              <div style={{ paddingLeft: 14, paddingTop: 4 }}>
                {floors.map(f => {
                  const fKey = bKey + '|' + f;
                  const fOpen = expanded[fKey] !== false;
                  const processes = Object.keys(tree[b][f]).sort();
                  const fCellCount = processes.reduce((a, p) => a + tree[b][f][p].length, 0);
                  return (
                    <div key={f} style={{ marginBottom: 4 }}>
                      <div onClick={() => toggle(fKey)}
                           style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                                    background: '#fff7ed', color: '#9a3412', borderRadius: 5, cursor: 'pointer',
                                    fontWeight: 700, fontSize: 12, border: '1px solid #fed7aa' }}>
                        {fOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                        <LayersIcon fontSize="small" />
                        <span>{f}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#c2410c' }}>
                          {processes.length} processes · {fCellCount} cells
                        </span>
                      </div>
                      {fOpen && (
                        <div style={{ paddingLeft: 16, paddingTop: 4 }}>
                          {processes.map(p => {
                            const pKey = fKey + '|p:' + p;
                            const pOpen = expanded[pKey] !== false; // default open
                            const cells = tree[b][f][p].slice().sort((a, c) => Number(a.code) - Number(c.code));
                            const pColor = SHOP_COLORS[p] || '#6b7280';
                            const pMachines = cells.reduce((a, g) => a + (Array.isArray(g.machines) ? g.machines.length : 0), 0);
                            return (
                              <div key={p} style={{ marginBottom: 4 }}>
                                <div onClick={() => toggle(pKey)}
                                     style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                                              background: pColor + '14', color: pColor, borderRadius: 5, cursor: 'pointer',
                                              fontWeight: 800, fontSize: 12, borderLeft: '3px solid ' + pColor }}>
                                  {pOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                  <PrecisionManufacturingIcon fontSize="small" />
                                  <span>{niceShop(p)}</span>
                                  <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, opacity: 0.85 }}>
                                    {cells.length} cell{cells.length !== 1 ? 's' : ''} · {pMachines} machine{pMachines !== 1 ? 's' : ''}
                                  </span>
                                </div>
                                {pOpen && (
                                  <div style={{ paddingLeft: 18, paddingTop: 4 }}>
                                    {cells.map(g => {
                                      const cKey = pKey + '|' + g.id;
                                      const cOpen = expanded[cKey] === true;
                                      const machines = Array.isArray(g.machines) ? g.machines : [];
                                      return (
                                        <div key={g.id} style={{ marginBottom: 4 }}>
                                          <div onClick={() => toggle(cKey)}
                                               style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                                                        background: '#fff', color: '#111827', borderRadius: 5, cursor: 'pointer',
                                                        fontSize: 12, border: '1px solid #e5e7eb' }}>
                                            {cOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                            <span style={{ fontWeight: 800, fontFamily: 'monospace', color: '#6b7280', minWidth: 22 }}>
                                              {g.code}
                                            </span>
                                            <span style={{ fontWeight: 700 }}>{g.name}</span>
                                            <span style={{ marginLeft: 'auto', fontSize: 10, color: '#6b7280' }}>
                                              {machines.length} machine{machines.length !== 1 ? 's' : ''}
                                            </span>
                                          </div>
                                          {cOpen && (
                                            <div style={{ paddingLeft: 22, paddingTop: 4,
                                                            display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                              {machines.length === 0 ? (
                                                <span style={{ fontSize: 11, color: '#9ca3af', padding: '6px 0' }}>(no machines)</span>
                                              ) : machines.map((m, i) => {
                                                const name = typeof m === 'string' ? m : (m?.name || '(unnamed)');
                                                return (
                                                  <span key={i}
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                                                  padding: '3px 8px', background: '#eff6ff', color: '#1e3a8a',
                                                                  border: '1px solid #93c5fd', borderRadius: 4,
                                                                  fontSize: 11, fontWeight: 700, fontFamily: 'monospace' }}>
                                                    <PrecisionManufacturingIcon sx={{ fontSize: 11 }} />
                                                    {name}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}



export default MachineGroup;
